import React, {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import useToastContext from "hooks/useToastContext";
import { useForm } from "react-hook-form";
import { isEmpty, orderBy, get as __get } from "lodash";
import OInputField from "components/reusable/OInputField";
import { useTranslation } from "react-i18next";
import ODatePicker from "components/shared/datePicker/ODatePicker";
import { apiGet, apiPost, apiPut } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import OSelect from "components/reusable/OSelect";
import formValidation from "utils/formValidation";
import OImage from "components/reusable/OImage";
import { AiFillCamera } from "react-icons/ai";
import defaultDeveloperImage from "../../assets/images/defaultDeveloperImage.jpg";
import { useLocation, useNavigate } from "react-router-dom";
import OAmenities from "components/reusable/OAmenities";
import { TagsInput } from "react-tag-input-component";
import OPropertyCategories from "components/reusable/OPropertyCategories";
import {
  AREA_BOUND,
  AREA_REGION,
  COUNTRY_RESTRICTIONS,
  PROPERTY_ALLOWED_PHOTO_EXT,
} from "../../utils/constants";
import photoClose from "../../assets/images/photo_close.png";
import OMapContainer from "components/reusable/OMapContainer";
import AuthContext from "context/AuthContext";
import helper from "utils/helpers";
import ReactQuill from "react-quill";
import { FaFilePdf } from "react-icons/fa";
import OFileInput from "components/reusable/OFileInput";
import ErrorMessage from "components/ErrorMessage";
import { BiDownload } from "react-icons/bi";

const imageCount = 6;
const imageCountMax = 10;
const AddProjectFloor = () => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [editProperty, setEditProperty] = useState(location?.state?.item ?? "");
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true, defaultValues: {} });
  const [projects, setProjects] = useState([]);
  const getProjects = useCallback(
    async (projectId = "") => {
      try {
        const payload = {};
        const path = apiPath.projectPath + "/all-projects";
        const result = await apiGet(path, payload);
        const records = result?.data?.results;
        setProjects(records);
        startTransition(() => {
          setValue("projectId", projectId);
        });
      } catch (error) {
        console.error("error in get all users list==>>>>", error.message);
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (editProperty) {
      getProjects(editProperty.projectId);
    } else {
      getProjects();
    }
  }, [getProjects, editProperty]);

  useEffect(() => {
    if (editProperty) {
      console.log("editProperty", editProperty);
      setValue("projectId", editProperty.projectId);
      setValue("floorName", editProperty.floorName);
      setValue("floorNameAr", editProperty.floorNameAr);
      setValue("floorId", editProperty.floorId);
      setValue("viewOnMap", editProperty.viewOnMap);
      setValue("coordinates", editProperty.coordinates);
    }
  }, [editProperty, setValue]);

  const handleSubmitForm = async (data) => {
    let formData = new FormData();
    if (editProperty) {
      formData.append("_id", editProperty._id);
    }
    formData.append("projectId", data.projectId);
    formData.append("floorName", data.floorName);
    formData.append("floorNameAr", data.floorNameAr);
    formData.append("floorId", data.floorId);
    formData.append("viewOnMap", data.viewOnMap);
    formData.append("coordinates", JSON.stringify(data.coordinates));
    if (data.floorImage) {
      formData.append("floorImage", data.floorImage[0]);
    }
    if (data.geoJsonFile) {
      formData.append("geoJsonFile", data.geoJsonFile[0]);
    }
    try {
      const { data: response } = await apiPost(apiPath.projectFloor, formData);

      if (response?.success) {
        notification.success(response?.message);
        navigate("/project-floors");
      } else {
        notification.error(response?.message);
      }
    } catch (error) {
      notification.error(error?.message);
    }
  };

  function isValidLatitude(lat) {
    const num = parseFloat(lat);
    return !isNaN(num) && num >= -90 && num <= 90;
  }

  function isValidLongitude(lng) {
    const num = parseFloat(lng);
    return !isNaN(num) && num >= -180 && num <= 180;
  }

  return (
    <>
      <div className="basic_info px-4 ">
        <form className="py-4" onSubmit={handleSubmit(handleSubmitForm)}>
          <div className="">
            <div className="grid grid-rows-1 grid-cols-1 gap-4">
              <div className="mb-3 ">
                <label
                  for="projectId"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  {t("SELECT_PROJECT")}
                </label>
                <select
                  name="projectId"
                  id="projectId"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  register={register("projectId", {
                    required: "Please select project.",
                  })}
                  value={editProperty?.projectId}
                  onChange={(e) => {
                    setValue("projectId", e.target.value);
                    trigger("projectId");
                  }}
                >
                  <option value="">{t("SELECT_PROJECT")}</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.title}
                    </option>
                  ))}
                </select>
                <ErrorMessage message={errors?.projectId?.message} />
              </div>
            </div>
            <div className="grid grid-rows-1 grid-cols-2 gap-4">
              <div className="mb-3">
                <label className="block">
                  <OInputField
                    wrapperClassName="relative z-0   w-full group"
                    name="floorName"
                    inputLabel={
                      <>
                        {t("O_FLOOR_NAME")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    type="text"
                    register={register("floorName", {
                      required: "Please enter floor name.",
                      validate: (value) => {
                        return value.trim()
                          ? true
                          : "White spaces not allowed.";
                      },
                    })}
                    errors={errors}
                  />
                </label>
              </div>
              <div className="mb-3">
                <label className="block">
                  <OInputField
                    wrapperClassName="relative z-0   w-full group"
                    name="floorNameAr"
                    inputLabel={
                      <>
                        {t("O_FLOOR_NAME_AR")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    type="text"
                    register={register("floorNameAr", {
                      required: "Please enter floor name.",
                      validate: (value) => {
                        return value.trim()
                          ? true
                          : "White spaces not allowed.";
                      },
                    })}
                    errors={errors}
                  />
                </label>
              </div>
              <div className="mb-3">
                <label className="block">
                  <OInputField
                    wrapperClassName="relative z-0   w-full group"
                    name="floorId"
                    inputLabel={
                      <>
                        {t("O_FLOOR_ID")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    type="text"
                    register={register("floorId", {
                      required: "Please enter floor id.",
                      validate: (value) => {
                        return value.trim()
                          ? true
                          : "White spaces not allowed.";
                      },
                    })}
                    errors={errors}
                  />
                </label>
              </div>
              <div className="mb-3">
                <label className="block">
                  <OSelect
                    wrapperClassName="relative z-0   w-full group"
                    name="viewOnMap"
                    inputLabel={
                      <>
                        {t("O_VIEW_ON_MAP")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    type="text"
                    register={register("viewOnMap", {
                      required: "Please select view on map.",
                    })}
                    defaultValue={t("SELECT_VIEW_ON_MAP")}
                    errors={errors}
                    options={[
                      { key: "Yes", value: true },
                      { key: "No", value: false },
                    ]}
                  />
                </label>
              </div>
            </div>
            <div className="mb-3">
              <label className="block">
                {t("O_COORDINATES")}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4 items-center">
                <label>A</label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0 w-full group"
                    name="coordinates.0.latitude"
                    type="text"
                    placeholder="Latitude"
                    register={register("coordinates.0.latitude", {
                      required: "Please enter latitude.",
                      validate: (value) => {
                        return isValidLatitude(value)
                          ? true
                          : "Please enter valid latitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[0]?.latitude?.message}
                  />
                </label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0   w-full group"
                    name="coordinates.0.longitude"
                    type="text"
                    placeholder="Longitude"
                    register={register("coordinates.0.longitude", {
                      required: "Please enter longitude.",
                      validate: (value) => {
                        return isValidLongitude(value)
                          ? true
                          : "Please enter valid longitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[0]?.longitude?.message}
                  />
                </label>
              </div>
              <div className="flex gap-4 items-center w-full">
                <label>B</label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0 w-full group"
                    name="coordinates.1.latitude"
                    type="text"
                    placeholder="Latitude"
                    register={register("coordinates.1.latitude", {
                      required: "Please enter latitude.",
                      validate: (value) => {
                        return isValidLatitude(value)
                          ? true
                          : "Please enter valid latitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[1]?.latitude?.message}
                  />
                </label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0 w-full group"
                    name="coordinates.1.longitude"
                    type="text"
                    placeholder="Longitude"
                    register={register("coordinates.1.longitude", {
                      required: "Please enter longitude.",
                      validate: (value) => {
                        return isValidLongitude(value)
                          ? true
                          : "Please enter valid longitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[1]?.longitude?.message}
                  />
                </label>
              </div>
              <div className="flex gap-4 items-center w-full">
                <label>C</label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0 w-full group"
                    name="coordinates.2.latitude"
                    type="text"
                    placeholder="Latitude"
                    register={register("coordinates.2.latitude", {
                      required: "Please enter latitude.",
                      validate: (value) => {
                        return isValidLatitude(value)
                          ? true
                          : "Please enter valid latitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[2]?.latitude?.message}
                  />
                </label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0 w-full group"
                    name="coordinates.2.longitude"
                    type="text"
                    placeholder="Longitude"
                    register={register("coordinates.2.longitude", {
                      required: "Please enter longitude.",
                      validate: (value) => {
                        return isValidLongitude(value)
                          ? true
                          : "Please enter valid longitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[2]?.longitude?.message}
                  />
                </label>
              </div>
              <div className="flex gap-4 items-center w-full">
                <label>D</label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0 w-full group"
                    name="coordinates.3.latitude"
                    type="text"
                    placeholder="Latitude"
                    register={register("coordinates.3.latitude", {
                      required: "Please enter latitude.",
                      validate: (value) => {
                        return isValidLatitude(value)
                          ? true
                          : "Please enter valid latitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[3]?.latitude?.message}
                  />
                </label>
                <label className="block flex-1">
                  <OInputField
                    wrapperClassName="relative z-0 w-full group"
                    name="coordinates.3.longitude"
                    type="text"
                    placeholder="Longitude"
                    register={register("coordinates.3.longitude", {
                      required: "Please enter longitude.",
                      validate: (value) => {
                        return isValidLongitude(value)
                          ? true
                          : "Please enter valid longitude.";
                      },
                    })}
                  />
                  <ErrorMessage
                    message={errors["coordinates"]?.[3]?.longitude?.message}
                  />
                </label>
              </div>
            </div>
            <div className="grid grid-rows-1 grid-cols-2 gap-4">
              <div className="mb-3">
                <label className="block">
                  <OFileInput
                    wrapperClassName="relative z-0   w-full group"
                    name="floorImage"
                    inputLabel={
                      <>
                        {t("O_FLOOR_IMAGE")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    register={register("floorImage", {
                      required: editProperty
                        ? false
                        : "Please upload floor image.",
                      validate: (value) => {
                        if (editProperty) {
                          return true;
                        }
                        let file = value[0];
                        if (file && file.type.includes("image")) {
                          return true;
                        }
                        return "Please upload image file.";
                      },
                    })}
                    errors={errors}
                    accept="image/png, image/jpg, image/jpeg"
                  />
                </label>
                {editProperty?.floorImage && (
                  <OImage
                    src={`https://objectstorage.me-jeddah-1.oraclecloud.com/n/axxblwxnjwnb/b/mada-properties-live/o/${editProperty?.floorImage}`}
                    alt="floorImage"
                    className="w-20 h-20 object-cover border border-gray-300 rounded-md p-1 mt-2"
                  />
                )}
              </div>
              <div className="mb-3">
                <label className="block">
                  <OFileInput
                    wrapperClassName="relative z-0   w-full group"
                    name="geoJsonFile"
                    inputLabel={
                      <>
                        {t("O_GEO_JSON_FILE")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    register={register("geoJsonFile", {
                      required: editProperty
                        ? false
                        : "Please upload geo json file.",
                      validate: (value) => {
                        if (editProperty) {
                          return true;
                        }
                        let file = value[0];
                        if (file) {
                          return true;
                        }
                        return "Please upload geo json file.";
                      },
                    })}
                    errors={errors}
                  />
                </label>
                {editProperty?.geoJsonFile && (
                  <a
                    href={`https://objectstorage.me-jeddah-1.oraclecloud.com/n/axxblwxnjwnb/b/mada-properties-live/o/${editProperty?.geoJsonFile}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="text-blue-500 hover:text-blue-700 mt-2 inline-flex items-center gap-2"
                  >
                    <BiDownload className="w-4 h-4" />
                    {t("O_VIEW_GEO_JSON_FILE")}
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="border-t mt-10 py-10 text-center">
            <button className="bg-lime-500 hover:bg-[#8FC150] cursor-pointer   text-white  font-normal active:bg-slate-100 text-md px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150">
              {t("SAVE_&_CONTINUE")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddProjectFloor;
